import { getLanguage, resolveSupportedLanguage } from '@/constants/languages';

/**
 * Per-script webfonts, keyed off the registry's `script` (FR-26).
 *
 * DM Sans covers Latin and Cyrillic only; the other scripts fall through to the
 * OS, which on stock Windows is tofu for Khmer and Bengali. Fetched lazily
 * rather than linked in `index.html` so an English visitor's font payload is
 * unchanged (NFR-1) — these are ~6 large CJK/Indic families.
 */
const FONT_BY_SCRIPT: Record<string, string> = {
  Hans: 'Noto Sans SC',
  Hant: 'Noto Sans TC',
  Jpan: 'Noto Sans JP',
  Kore: 'Noto Sans KR',
  Arab: 'Noto Sans Arabic',
  Beng: 'Noto Sans Bengali',
  Khmr: 'Noto Sans Khmer',
};

/** Scripts the bundled UI face already covers. */
const BUNDLED_SCRIPTS = new Set(['Latn', 'Cyrl']);

/** The family the interface asks for; injected faces alias onto it. */
const UI_FAMILY = 'DM Sans';

const WEIGHTS = '400;500;600;700';

const requested = new Set<string>();

const cssUrl = (family: string): string =>
  `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@${WEIGHTS}&display=swap`;

/**
 * Fetch a Noto family's `@font-face` rules and re-declare them under `DM Sans`.
 *
 * Composite-font technique: one family can have many `@font-face` rules and the
 * browser picks per glyph, so no component needs to know a locale is active.
 * The alternative — appending the family at ~36 `fontFamily` call sites — would
 * also have to out-specify Emotion's injected classes.
 *
 * A failed fetch leaves the page as-is: system fallback.
 */
const injectScriptFont = async (script: string, family: string): Promise<void> => {
  try {
    const response = await fetch(cssUrl(family));
    if (!response.ok) return;
    const css = await response.text();

    const aliased = css.replace(
      new RegExp(`font-family:\\s*'${family}'`, 'g'),
      `font-family: '${UI_FAMILY}'`
    );
    // Nothing rewritten means nothing would reference the injected family.
    if (aliased === css) return;

    const style = document.createElement('style');
    style.dataset.vmLocaleFont = script;
    style.textContent = aliased;
    document.head.appendChild(style);
  } catch {
    // Offline, or blocked by a partner page's CSP — the system face stands in.
  }
};

/**
 * Ensure the webfont for `language`'s script is in the document. Idempotent, and
 * a no-op for Latin/Cyrillic locales, so it's cheap to call on every switch.
 */
export const loadFontsForLanguage = (rawLanguage: string | null | undefined): void => {
  if (typeof document === 'undefined' || typeof fetch === 'undefined') return;

  const code = resolveSupportedLanguage(rawLanguage ?? null);
  const script = code ? getLanguage(code)?.script : undefined;
  if (!script || BUNDLED_SCRIPTS.has(script)) return;

  const family = FONT_BY_SCRIPT[script];
  if (!family || requested.has(script)) return;
  requested.add(script);

  void injectScriptFont(script, family);
};
